// @flow strict

import * as React from 'react'
import cx from 'classnames'
import { useState, useReducer } from 'react'
import { Input, Button } from '@mondough/public-web-components'

import styles from './USSignupForm.module.scss'

const authenticateClientRequest = () => {
  return fetch(`${process.env.GATSBY_API_BASE_PATH}/oauth2/token`, {
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: process.env.GATSBY_CLIENT_ID,
      client_secret: process.env.GATSBY_CLIENT_SECRET,
    }),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      Accept: 'application/json',
    },
  })
}
const postWaitlistSignup = (
  token: string,
  name: string,
  email: string,
  zipcode: string,
  hasUKAccount: boolean,
  utm_source: string,
  utm_medium: string,
  utm_campaign: string,
  utm_term: string,
  utm_content: string,
) => {
  return fetch(`${process.env.GATSBY_API_BASE_PATH}/waitlist/signup/usa`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      name,
      email,
      zipcode,
      has_uk_account: hasUKAccount,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
    }),
  })
}

type FormState = {
  ok: boolean | null,
  message: string,
}

function formStateReducer(
  state: FormState,
  status: 'error' | 'success',
): FormState {
  switch (status) {
    case 'success':
      return {
        ok: true,
        message:
          "Thanks for signing up! You'll receive a confirmation email shortly",
      }
    case 'error':
      return {
        ok: false,
        message: 'Sorry, something went wrong',
      }
    default:
      return state
  }
}

type SignupFormProps = {
  dark?: boolean,
  legend?: string,
  orientation: string,
}

const getUrlQueryParam = (param: string): string => {
  // Parse query parameters
  const queryParams = {}
  window.location.search.replace(/[?&]+([^=&]+)=?([^&]*)?/gi, function(
    m,
    key,
    value,
  ) {
    queryParams[key] = value !== undefined ? value : ''
  })

  // Return the requested parameter
  return param && queryParams[param]
    ? decodeURIComponent(queryParams[param])
    : ''
}

const SignupForm = ({ dark, legend, orientation }: SignupFormProps) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [zipcode, setZipCode] = useState('')
  const [hasUKAccount, setUKAccount] = useState(false)
  const [formState, formStateDispatch] = useReducer(formStateReducer, {
    ok: null,
    message: '',
  })

  const onSubmit = async (evt: SyntheticEvent<HTMLFormElement>) => {
    evt.preventDefault()

    try {
      const authenticated = await authenticateClientRequest()
      // 4XXs aren't treated as errors so we need to check to see
      // whether the response was OK
      if (!authenticated.ok) return formStateDispatch('error')

      const { access_token } = await authenticated.json()
      const signup = await postWaitlistSignup(
        access_token,
        name,
        email,
        zipcode,
        hasUKAccount,
        getUrlQueryParam('utm_source'),
        getUrlQueryParam('utm_medium'),
        getUrlQueryParam('utm_campaign'),
        getUrlQueryParam('utm_term'),
        getUrlQueryParam('utm_content'),
      )

      // 4XXs aren't treated as errors so we need to check to see
      // whether the response was OK
      if (!signup.ok) return formStateDispatch('error')

      // Trigger FB Pixel event
      // https://docs.google.com/document/d/1n4TE0572ipQtPty3lADYGZf3U-15y7cbxn8c_6qTkaE/edit
      window.fbq && window.fbq('track', 'StartTrial')

      return formStateDispatch('success')
    } catch (err) {
      console.error(err)
      return formStateDispatch('error')
    }
  }

  if (formState.ok === true)
    return (
      <p className={styles.success} role="alert">
        {formState.message}
      </p>
    )

  return (
    <form
      className={cx(styles.form, styles[orientation], { [styles.dark]: dark })}
      onSubmit={onSubmit}
    >
      <fieldset>
        {legend && <legend className={styles.legend}>{legend}</legend>}
        {formState.ok === false && (
          <p className={styles.error} role="alert">
            {formState.message}
          </p>
        )}
        <ul className={styles.list}>
          <li className={styles.item}>
            <Input
              name="name"
              label="Your name"
              required={true}
              onChangeHandler={value =>
                typeof value !== 'boolean' && setName(value)
              }
              placeholder="Jane Doe"
            />
          </li>
          <li className={styles.item}>
            <Input
              name="email"
              label="Email address"
              type="email"
              required={true}
              onChangeHandler={value =>
                typeof value !== 'boolean' && setEmail(value)
              }
              placeholder="jane@example.com"
            />
          </li>
          <li className={styles.item}>
            <Input
              name="zipcode"
              label="ZIP"
              required={true}
              onChangeHandler={value =>
                typeof value !== 'boolean' && setZipCode(value)
              }
              pattern="^[0-9]{5}(?:-[0-9]{4})?$"
              placeholder="90210"
            />
          </li>
          <li className={styles.item}>
            <Input
              name="hasUKAccount"
              type="checkbox"
              label="I’m already a UK Monzo customer"
              onChangeHandler={checked =>
                typeof checked !== 'string' && setUKAccount(checked)
              }
            />
          </li>
          <li className={styles.item}>
            <Button>Join the waitlist</Button>
          </li>
        </ul>
      </fieldset>
    </form>
  )
}

export default SignupForm
